
import apiService from "../../services/apiServices";
import { useEffect, useState, useMemo } from "react";
import { Button, Form, InputGroup, Card, Badge, Row, Col, Table, Alert } from "react-bootstrap";
import BrandModal from "../core/components/Brand/BrandModal";
import { FaSlidersH, FaPlus, FaMinus, FaSearch, FaTimes, FaCheckSquare, FaSquare, FaBarcode } from "react-icons/fa";

const REASONS = ["Inventory Correction", "Damaged / Expired", "Theft / Loss", "Found Item", "Other"];

const StockAdjustmentModal = ({ item, variantId, onClose }) => {
  const [adjustmentType, setAdjustmentType] = useState("Deduct"); // Default Deduct (safer)
  const [nonSerQty, setNonSerQty] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  // Variant State & Loading
  const [variantsList, setVariantsList] = useState(item?.variants || []);
  const [selectedVariantId, setSelectedVariantId] = useState(variantId || "");

  useEffect(() => {
    if (variantId) {
      setSelectedVariantId(variantId);
    } else if (item?.variants?.length === 1) {
      setSelectedVariantId(item.variants[0]._id);
    }
  }, [variantId, item]);

  useEffect(() => {
    if (item?.variants && item.variants.length > 0) {
      setVariantsList(item.variants);
    } else if (item?._id && (item.hasVariants || item.variantCount > 0)) {
      apiService.get(`/stock/items/${item._id}`).then(res => {
        if (res.data?.variants) {
          setVariantsList(res.data.variants);
          if (variantId) {
            setSelectedVariantId(variantId);
          } else if (res.data.variants.length === 1) {
            setSelectedVariantId(res.data.variants[0]._id);
          }
        }
      }).catch(err => console.error("Failed to load variants", err));
    }
  }, [item, variantId]);

  const hasVariants = Boolean(item?.hasVariants || variantsList.length > 0);
  const selectedVariant = variantsList.find(v => String(v._id) === String(selectedVariantId));

  // New Fields for "Add"
  const [unitCost, setUnitCost] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [condition, setCondition] = useState("Brand New");

  useEffect(() => {
    // Auto-fill defaults from Item Master or Selected Variant when "Add" is selected
    if (adjustmentType === "Add") {
      if (selectedVariant) {
        setUnitCost(selectedVariant.lastUnitCost || item?.lastUnitCost || 0);
        setSellingPrice(selectedVariant.defaultSellingPrice || item?.pricing?.sellingPrice || item?.lastSellingPrice || 0);
      } else if (item) {
        setUnitCost(item.lastUnitCost || 0);
        setSellingPrice(item.pricing?.sellingPrice || item.lastSellingPrice || 0);
      }
    }
  }, [adjustmentType, item, selectedVariant]);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await apiService.get("/suppliers");
      const supplierList = Array.isArray(res.data) ? res.data : (res.data.suppliers || []);
      setSuppliers(supplierList);
    } catch (err) {
      console.error("Failed to fetch suppliers", err);
    }
  };

  // Serialized State
  const [selectedSerials, setSelectedSerials] = useState([]); // For Deduct
  const [newSerials, setNewSerials] = useState([{ serialNumber: "" }]); // For Add
  const [searchTerm, setSearchTerm] = useState("");

  const isSerialized = item?.serialized;
  const currentStock = hasVariants
    ? (selectedVariant?.stockTracking?.availableForSale ?? selectedVariant?.availableQty ?? 0)
    : (item?.totalAvailable || 0);

  // Filter available serials for Deduct list
  const availableSerials = useMemo(() => {
    if (!isSerialized || !item?.inStock) return [];
    return item.inStock.filter(s => {
      const matchVariant = !selectedVariantId || String(s.variant_id) === String(selectedVariantId);
      const matchSearch = s.serialNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.batch_number && s.batch_number.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchVariant && matchSearch;
    });
  }, [item, searchTerm, isSerialized, selectedVariantId]);

  const handleToggleSerial = (sn) => {
    setSelectedSerials(prev =>
      prev.includes(sn) ? prev.filter(s => s !== sn) : [...prev, sn]
    );
  };

  const handleAddNewSerialField = () => {
    setNewSerials([...newSerials, { serialNumber: "" }]);
  };

  const handleNewSerialChange = (index, val) => {
    const updated = [...newSerials];
    updated[index].serialNumber = val;
    setNewSerials(updated);
  };

  const handleRemoveNewSerialField = (index) => {
    setNewSerials(newSerials.filter((_, i) => i !== index));
  };

  const generateRandomSerial = (index) => {
    const prefix = item?.itemName?.slice(0, 3).toUpperCase() || "SN";
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    const timestamp = Date.now().toString().slice(-4);
    const sn = `${prefix}-${random}-${timestamp}`;

    const updated = [...newSerials];
    updated[index].serialNumber = sn;
    setNewSerials(updated);
  };

  const calculatePayload = () => {
    const basePayload = {
      // Include optional fields for "Add" logic (backend handles if ignored for Deduct)
      unitCost: parseFloat(unitCost) || 0,
      sellingPrice: parseFloat(sellingPrice) || 0,
      supplierId: supplierId || null,
      condition: condition || "Brand New"
    };

    if (!isSerialized) {
      return { ...basePayload, adjustmentQty: parseInt(nonSerQty) || 0 };
    }
    if (adjustmentType === "Deduct") {
      return { ...basePayload, targetSerialNumbers: selectedSerials, adjustmentQty: selectedSerials.length };
    } else {
      const validSerials = newSerials.filter(s => s.serialNumber.trim().length > 0);
      return {
        ...basePayload,
        newSerialNumbers: validSerials.map(s => ({
          ...s,
          // Allow per-serial override if we added fields there, otherwise fallback to global inputs
          unitCost: parseFloat(unitCost) || s.unitCost || 0,
          sellingPrice: parseFloat(sellingPrice) || s.sellingPrice || 0
        })),
        adjustmentQty: validSerials.length
      };
    }
  };

  const handleSubmit = async () => {
    const payload = calculatePayload();

    if (hasVariants && !selectedVariantId) return alert("Please select a variant.");
    if (payload.adjustmentQty <= 0) return alert("Please specify a quantity or select items.");
    if (!reason) return alert("Please provide a reason.");

    try {
      setLoading(true);
      await apiService.post("/stock/adjust", {
        itemId: item._id,
        variantId: selectedVariantId || null,
        adjustmentType,
        reason,
        ...payload
      });
      onClose(true);
    } catch (error) {
      console.error("Adjustment Failed", error);
      alert(error.response?.data?.message || "Adjustment Failed");
    } finally {
      setLoading(false);
    }
  };

  // Render Logic Helpers
  const renderSerializedDeduct = () => (
    <div className="border rounded bg-white overflow-hidden d-flex flex-column" style={{ maxHeight: 300 }}>
      <div className="p-2 border-bottom bg-light">
        <InputGroup size="sm">
          <InputGroup.Text className="bg-white border-end-0"><FaSearch className="text-muted" /></InputGroup.Text>
          <Form.Control
            placeholder="Search Serial Number..."
            className="border-start-0"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </InputGroup>
        <div className="mt-2 d-flex justify-content-between align-items-center">
          <small className="text-muted">{selectedSerials.length} selected</small>
          {selectedSerials.length > 0 && <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => setSelectedSerials([])}>Clear</Button>}
        </div>
      </div>
      <div className="overflow-auto p-0">
        {availableSerials.length === 0 ? (
          <div className="text-center py-4 text-muted small">No available serials found matching search.</div>
        ) : (
          availableSerials.map(s => (
            <div
              key={s.serialNumber}
              className={`d-flex align-items-center p-2 border-bottom cursor-pointer ${selectedSerials.includes(s.serialNumber) ? "bg-primary-subtle" : "hover-bg-light"}`}
              onClick={() => handleToggleSerial(s.serialNumber)}
              style={{ cursor: 'pointer' }}
            >
              <div className={`me-3 text-${selectedSerials.includes(s.serialNumber) ? "primary" : "muted"}`}>
                {selectedSerials.includes(s.serialNumber) ? <FaCheckSquare /> : <FaSquare />}
              </div>
              <div>
                <div className="fw-bold font-monospace">{s.serialNumber}</div>
                <div className="small text-muted">{s.batch_number}</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderSerializedAdd = () => (
    <div className="border rounded bg-light p-3" style={{ maxHeight: 300, overflowY: 'auto' }}>
      <label className="small fw-bold text-muted mb-2">New Serial Numbers</label>
      {newSerials.map((s, idx) => (
        <InputGroup size="sm" className="mb-2" key={idx}>
          <InputGroup.Text className="bg-light fw-bold">#{idx + 1}</InputGroup.Text>
          <Form.Control
            placeholder="Scan/Type Serial..."
            value={s.serialNumber}
            onChange={(e) => handleNewSerialChange(idx, e.target.value)}
            autoFocus={idx === newSerials.length - 1} // Auto-focus last added
            className="font-monospace fw-bold"
          />
          <Button
            variant="outline-secondary"
            title="Generate Serial"
            onClick={() => generateRandomSerial(idx)}
          >
            <FaBarcode />
          </Button>
          {newSerials.length > 1 && (
            <Button variant="outline-danger" onClick={() => handleRemoveNewSerialField(idx)}><FaTimes /></Button>
          )}
        </InputGroup>
      ))}
      <Button variant="outline-primary" size="sm" className="w-100 dashed-border" onClick={handleAddNewSerialField}>
        <FaPlus /> Add Another Serial
      </Button>
    </div>
  );

  const isVariantValid = !hasVariants || Boolean(selectedVariantId);
  const qtyNum = parseInt(nonSerQty, 10);
  const isQtyValid = isSerialized
    ? (adjustmentType === "Deduct"
        ? selectedSerials.length > 0
        : newSerials.some(s => s.serialNumber && s.serialNumber.trim().length > 0))
    : (!isNaN(qtyNum) && qtyNum > 0);
  const isReasonValid = Boolean(reason && reason.trim().length > 0);
  const isPricingValid = adjustmentType !== "Add" || (
    !isNaN(parseFloat(sellingPrice)) && parseFloat(sellingPrice) >= 0 &&
    !isNaN(parseFloat(unitCost)) && parseFloat(unitCost) >= 0
  );
  const isSubmitDisabled = loading || !isVariantValid || !isQtyValid || !isReasonValid || !isPricingValid;

  return (
    <BrandModal
      show={true}
      onHide={() => onClose(false)}
      title="Adjust Stock Level"
      subtitle={item ? `${item.itemName}` : "Stock Adjustment"}
      icon={<FaSlidersH />}
      size="lg"
      footer={
        <div className="d-flex justify-content-end align-items-center gap-2 w-100">
          {!isVariantValid && (
            <small className="text-danger me-auto">Select a variant to proceed</small>
          )}
          {isVariantValid && !isQtyValid && (
            <small className="text-danger me-auto">Enter quantity &gt; 0</small>
          )}
          {isVariantValid && isQtyValid && !isReasonValid && (
            <small className="text-muted me-auto">Choose an adjustment reason</small>
          )}
          <Button variant="light" onClick={() => onClose(false)} disabled={loading}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitDisabled}>
            {loading ? "Processing..." : "Confirm Adjustment"}
          </Button>
        </div>
      }
    >
      <div className="p-2">
        <Row className="g-4">
          <Col md={7}>
            <Card className="border shadow-sm h-100">
              <Card.Body>
                <h6 className="fw-bold mb-3">Adjustment Action</h6>

                <div className="d-flex gap-2 mb-4">
                  <Button
                    variant={adjustmentType === "Add" ? "success" : "white text-dark border"}
                    className={`flex-grow-1 ${adjustmentType === "Add" ? "shadow-sm active-success" : ""}`}
                    onClick={() => setAdjustmentType("Add")}
                  >
                    <FaPlus className="me-2" /> Add Stock
                  </Button>
                  <Button
                    variant={adjustmentType === "Deduct" ? "danger" : "white text-dark border"}
                    className={`flex-grow-1 ${adjustmentType === "Deduct" ? "shadow-sm active-danger" : ""}`}
                    onClick={() => setAdjustmentType("Deduct")}
                  >
                    <FaMinus className="me-2" /> Deduct Stock
                  </Button>
                </div>

                {/* VARIANT SELECTION (MANDATORY IF ITEM HAS VARIANTS) */}
                {hasVariants && (
                  <div className="mb-4 p-3 bg-light rounded border">
                    <Form.Group>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Form.Label className="small text-muted fw-bold mb-0">
                          Select Variant <span className="text-danger">*</span>
                        </Form.Label>
                        {!selectedVariantId && (
                          <Badge bg="danger" className="fw-normal">Required</Badge>
                        )}
                      </div>
                      <Form.Select
                        value={selectedVariantId}
                        onChange={(e) => {
                          const vId = e.target.value;
                          setSelectedVariantId(vId);
                          const matched = variantsList.find(v => String(v._id) === String(vId));
                          if (matched && adjustmentType === "Add") {
                            setUnitCost(matched.lastUnitCost || item?.lastUnitCost || 0);
                            setSellingPrice(matched.defaultSellingPrice || item?.pricing?.sellingPrice || item?.lastSellingPrice || 0);
                          }
                        }}
                        className="fw-bold"
                      >
                        <option value="">-- Choose Variant (Required) --</option>
                        {variantsList.map((v) => (
                          <option key={v._id} value={v._id}>
                            {v.variantName} {v.sku ? `[${v.sku}]` : ""} — In Stock: {v.stockTracking?.availableForSale ?? v.availableQty ?? 0}
                          </option>
                        ))}
                      </Form.Select>
                      {!selectedVariantId && (
                        <small className="text-danger mt-1 d-block">
                          This item has variants. You must select a variant to adjust stock.
                        </small>
                      )}
                    </Form.Group>
                  </div>
                )}

                {/* DYNAMIC INPUT AREA */}
                <div className="mb-4">
                  {isSerialized ? (
                    adjustmentType === "Deduct" ? renderSerializedDeduct() : renderSerializedAdd()
                  ) : (
                    <div>
                      <label className="small text-muted mb-1 d-block fw-bold">Quantity to {adjustmentType}</label>
                      <InputGroup size="lg">
                        <Form.Control
                          type="number"
                          min="1"
                          placeholder="0"
                          value={nonSerQty}
                          onChange={(e) => setNonSerQty(e.target.value)}
                          className="fw-bold text-center"
                        />
                      </InputGroup>
                    </div>
                  )}
                </div>

                {/* COST / PRICE / SUPPLIER (Only for Add) */}
                {adjustmentType === "Add" && (
                  <div className="mb-4 border-top pt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="small text-muted fw-bold">Stock Value & Source</label>
                      {parseFloat(unitCost) > parseFloat(sellingPrice) && (
                        <Badge bg="danger" className="animate-pulse">⚠ Negative Margin</Badge>
                      )}
                    </div>
                    {parseFloat(unitCost) > parseFloat(sellingPrice) && (
                      <Alert variant="danger" className="py-2 px-3 mb-3 small border-start border-4 border-danger rounded-0">
                        <strong>Price Protection Warning:</strong> Unit cost (Rs.{unitCost}) exceeds selling price (Rs.{sellingPrice}). This will result in a loss of Rs.{(unitCost - sellingPrice).toLocaleString()} per unit.
                      </Alert>
                    )}
                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small">Unit Cost</Form.Label>
                          <Form.Control
                            type="number"
                            size="sm"
                            value={unitCost}
                            onChange={(e) => setUnitCost(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="small">Selling Price</Form.Label>
                          <Form.Control
                            type="number"
                            size="sm"
                            value={sellingPrice}
                            onChange={(e) => setSellingPrice(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12} className="mt-2">
                        <Form.Group>
                          <Form.Label className="small">Supplier (Optional)</Form.Label>
                          <Form.Select
                            size="sm"
                            value={supplierId}
                            onChange={(e) => setSupplierId(e.target.value)}
                          >
                            <option value="">-- Adjustment (No Supplier) --</option>
                            {suppliers.map(sup => (
                              <option key={sup._id} value={sup._id}>{sup.businessName || sup.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={12} className="mt-2 text-start">
                        <Form.Label className="small fw-bold">Item Condition</Form.Label>
                        <div className="d-flex flex-wrap gap-1">
                          {["Brand New", "Used", "Open Box", "Refurbished", "Damaged"].map(c => (
                            <Badge
                              key={c}
                              bg={condition === c ? "info" : "light"}
                              text={condition === c ? "white" : "dark"}
                              className="cursor-pointer border py-1 px-2 fw-normal"
                              style={{ cursor: 'pointer', fontSize: '10px' }}
                              onClick={() => setCondition(c)}
                            >
                              {c}
                            </Badge>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </div>
                )}


                <div className="mb-3">
                  <label className="small text-muted mb-1 d-block fw-bold">Reason (Required)</label>
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {REASONS.map(r => (
                      <Badge
                        key={r}
                        bg={reason === r ? "primary" : "light"}
                        text={reason === r ? "white" : "dark"}
                        className="cursor-pointer border px-3 py-2 fw-normal"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setReason(r)}
                      >
                        {r}
                      </Badge>
                    ))}
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Details..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="bg-light"
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={5}>
            <Card className="border-0 bg-light h-100">
              <Card.Body className="d-flex flex-column justify-content-center align-items-center text-center">
                <div className="mb-4">
                  <small className="text-uppercase text-muted fw-bold letter-spacing-1">Current Stock</small>
                  <div className="display-6 fw-bold text-dark">{currentStock}</div>
                </div>

                <div className={`rounded-circle d-flex align-items-center justify-content-center mb-4 text-white shadow-sm ${adjustmentType === "Add" ? "bg-success" : "bg-danger"}`} style={{ width: 40, height: 40 }}>
                  {adjustmentType === "Add" ? <FaPlus /> : <FaMinus />}
                </div>

                <div className="mb-4">
                  <small className="text-uppercase text-muted fw-bold letter-spacing-1">Adjusting</small>
                  <div className={`display-6 fw-bold ${adjustmentType === "Add" ? "text-success" : "text-danger"}`}>
                    {isSerialized
                      ? (adjustmentType === "Deduct" ? selectedSerials.length : newSerials.filter(s => s.serialNumber).length)
                      : (parseInt(nonSerQty) || 0)
                    }
                  </div>
                  {isSerialized && <Badge bg="dark" className="mt-2">Serialized Items</Badge>}
                </div>

                <div className="border-top w-75 pt-4">
                  <small className="text-uppercase text-primary fw-bold letter-spacing-1">New Total (Est.)</small>
                  <div className="display-4 fw-bold text-primary">
                    {(() => {
                      const change = isSerialized
                        ? (adjustmentType === "Deduct" ? selectedSerials.length : newSerials.filter(s => s.serialNumber).length)
                        : (parseInt(nonSerQty) || 0);

                      return adjustmentType === "Add" ? currentStock + change : currentStock - change;
                    })()}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </BrandModal >
  );
};

export default StockAdjustmentModal;